import { Box, Tooltip, styled, IconButton } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import Agua from "@icons-rf/properties-item/agua.svg?react";
import Bath from "@icons-rf/properties-item/bath.svg?react";
import Bed from "@icons-rf/properties-item/bed.svg?react";
import FloorsTotal from "@icons-rf/properties-item/floors-total.svg?react";
import Carro from "@icons-rf/properties-item/carro.svg?react";
import Casa2 from "@icons-rf/properties-item/casa2.svg?react";
import HalfBath from "@icons-rf/properties-item/half-bath.svg?react";
import Pump from "@icons-rf/properties-item/pumps.svg?react";
import Measure from "@icons-rf/properties-item/medida.svg?react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import MoneyFormat from "@/src-refactor/modules/shared/helpers/moneyFormat.helper";
import { Button } from "@mui/base";
import { IPropertyDetailAdapter } from "@rf/property/module/adapters/PropertyDetailAdapter";

interface IMainDetailFeatureProps {
  features: IPropertyDetailAdapter["features"];
}

const ScrollContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  overflow: "hidden",
  marginTop: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5)
}));

const ScrollContent = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),

  overflowX: "auto",
  scrollBehavior: "smooth",
  "&::-webkit-scrollbar": {
    display: "none"
  },
  msOverflowStyle: "none",
  scrollbarWidth: "none"
}));

const ScrollArrow = styled(Button)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  zIndex: 1,
  transform: "translate(0, -50%)",
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  borderRadius: 0,
  boxShadow: "none",
  border: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    fontSize: 14,
    color: theme.palette.primary.main
  }
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  minWidth: "fit-content",
  "& svg": {
    width: 26,
    height: 26,
    color: theme.palette.grey[600]
  },
  "& span": {
    fontSize: 10,
    color: theme.palette.grey[600]
  }
}));

function MainDetailFeature(props: IMainDetailFeatureProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const checkScrollPosition = useCallback(() => {
    if (contentRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = contentRef.current;
      // Show left arrow only if not at the beginning
      setShowLeftArrow(scrollLeft > 0);
      // Show right arrow only if not at the end
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5); // 5px buffer for rounding errors
    }
  }, []);

  useEffect(() => {
    const checkScrollability = () => {
      if (contentRef.current) {
        const hasOverflow =
          contentRef.current.scrollWidth > contentRef.current.clientWidth;
        setShowScrollButtons(hasOverflow);
        if (hasOverflow) {
          checkScrollPosition();
        }
      }
    };

    checkScrollability();
    window.addEventListener("resize", checkScrollability);

    return () => {
      window.removeEventListener("resize", checkScrollability);
    };
  }, [checkScrollPosition]);

  useEffect(() => {
    const scrollElement = contentRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", checkScrollPosition);
      return () => {
        scrollElement.removeEventListener("scroll", checkScrollPosition);
      };
    }
  }, [checkScrollPosition]);

  const handleScroll = (direction: "left" | "right") => {
    if (contentRef.current) {
      const scrollAmount = 150;
      const currentScroll = contentRef.current.scrollLeft;
      contentRef.current.scrollLeft =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;
    }
  };

  const orderedItems = [
    {
      icon: Bed,
      value: props?.features?.bedrooms,
      label: "Habitaciones"
    },
    {
      icon: Bath,
      value: props?.features?.bathrooms,
      label: "Baños"
    },
    {
      icon: HalfBath,
      value: props?.features?.halfBathrooms,
      label: "Medios baños"
    },
    {
      icon: Carro,
      value: props?.features?.parkingSpots,
      label: "Parqueo"
    },
    {
      icon: Agua,
      value: props?.features?.floorLevel,
      label: "Nivel de piso"
    },
    {
      icon: FloorsTotal,
      value: props?.features?.floorsTotal,
      label: "Total de pisos"
    },
    {
      icon: Casa2,
      value: props?.features?.sqmLand ? MoneyFormat(props?.features?.sqmLand) : null,
      label: "Metros de Solar",
      sub: true
    },
    {
      icon: Measure,
      value: props?.features?.sqmConstruction
        ? MoneyFormat(props?.features?.sqmConstruction)
        : null,
      label: "Metros de Construcción",
      sub: true
    }
  ];

  const extraItems = [
    {
      icon: Pump,
      value: props?.features?.pumps,
      label: "Bombas"
    }
  ];

  const items = [...orderedItems, ...extraItems].filter(item => item.value);

  return (
    <ScrollContainer>
      {showScrollButtons && showLeftArrow && (
        <ScrollArrow
          onClick={() => handleScroll("left")}
          sx={{ left: -10 }}
          size="small"
        >
          <ArrowBackIosNewIcon />
        </ScrollArrow>
      )}
      <ScrollContent ref={contentRef}>
        {showScrollButtons && <div />}
        {items.map(item => (
          <Tooltip key={item.label} title={item.label}>
            <FeatureItem>
              {item.icon && <item.icon />}
              <span>
                {item.value}
                {item.sub && (
                  <span>
                    mt<sup>2</sup>
                  </span>
                )}
              </span>
            </FeatureItem>
          </Tooltip>
        ))}
        {showScrollButtons && <div />}
      </ScrollContent>
      {showScrollButtons && showRightArrow && (
        <ScrollArrow
          onClick={() => handleScroll("right")}
          sx={{ right: -10 }}
          size="small"
        >
          <ArrowForwardIosIcon />
        </ScrollArrow>
      )}
    </ScrollContainer>
  );
}

export default MainDetailFeature;
